import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, DownloadIcon, MapPin } from "lucide-react";
import VehicleMultiSelect, {
    filterBySelectedVehicles,
    formatPopoverDate,
    getRegNumber,
    toVehicleOptions
} from "./VehicleMultiSelect";
import styles from "./FrequencyBucketTable.module.css";
import CustomModal from "../../../../Shared/CustomModal";
import TablePagination from "./TablePagination";
import { BUCKET_COLUMNS } from "./frequencyBuckets";
import { useDeviceHealthContextData } from "./context/Context";
import { downloadCsv, getMinutesWiseData as getDepotVehicles } from "./DeviceHealthService";

export const DEPOT_VEHICLES_MODAL_ID = "FrequencyDepotVehicles";

const ROWS_PER_PAGE = 10; // fixed, not user-changeable
const COL_COUNT = 4 + BUCKET_COLUMNS.length;

const TONE_CLASS = {
    yellow: styles.tone_yellow,
    green: styles.tone_green,
    orange: styles.tone_orange,
    red: styles.tone_red
};

const displayValue = (val) =>
    val === null || val === undefined || val === "" ? "-" : val;

function StatusBadge({ frequency }) {
    const isHealthy = Number(frequency) > 80;
    return (
        <span className={`${styles.statusBadge} ${isHealthy ? styles.statusHealthy : styles.statusUnhealthy}`}>
        </span>
    );
}

/*
 * selection: { city, depot } — only that depot's vehicles are listed
 */
export default function DepotVehiclesPopover({ selection }) {
    const { GlobalFilter, lastRefreshDate } = useDeviceHealthContextData();
    const [vehicles, setVehicles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedVehicles, setSelectedVehicles] = useState([]);

    const { city, depot } = selection || {};

    // API 3: vehicles of the selected depot
    useEffect(() => {
        if (!depot) return;

        let cancelled = false;
        setIsLoading(true);
        setCurrentPage(1);
        setSelectedVehicles([]);

        getDepotVehicles({
            ...GlobalFilter,
            city: [{ label: city, value: city }],
            depot: [{ label: depot, value: depot }]
        })
            .then((res) => {
                if (!cancelled) setVehicles(res?.data || []);
            })
            .catch(() => {
                if (!cancelled) setVehicles([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [GlobalFilter, city, depot]);

    const vehicleOptions = useMemo(() => toVehicleOptions(vehicles), [vehicles]);

    const filteredVehicles = useMemo(
        () => filterBySelectedVehicles(vehicles, selectedVehicles),
        [vehicles, selectedVehicles]
    );

    const changeSelectedVehicles = (value) => {
        setSelectedVehicles(value);
        setCurrentPage(1);
    };

    // same CSV api as the Device Bucketing table: global filters + the opened depot
    const downloadVehicles = () => {
        downloadCsv(
            "downloadOverallFrequency",
            {
                ...GlobalFilter,
                city: [{ label: city, value: city }],
                depot: [{ label: depot, value: depot }]
            },
            `${depot} Depot Vehicles`
        ).catch(() => { });
    };

    const totalItems = filteredVehicles.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / ROWS_PER_PAGE));
    const safePage = Math.min(currentPage, totalPages);

    const paginatedVehicles = useMemo(() => {
        const start = (safePage - 1) * ROWS_PER_PAGE;
        return filteredVehicles.slice(start, start + ROWS_PER_PAGE);
    }, [filteredVehicles, safePage]);

    return (
        <CustomModal
            id={DEPOT_VEHICLES_MODAL_ID}
            title={depot ? `${depot} Depot · Vehicles` : "Vehicles"}
            size="modal-xl"
            header={true}
            closeBtnClass="btn ms_btn btn-default"
        >
            {depot && (
                <div className={styles.popoverBody}>
                    <div className={styles.popoverMeta}>
                        <span className={styles.metaChip}>
                            <MapPin size={11} />
                            {city} · {depot}
                        </span>
                        <span className={styles.metaChip}>
                            <CalendarDays size={11} />
                            {formatPopoverDate(lastRefreshDate)}
                        </span>
                        <VehicleMultiSelect
                            options={vehicleOptions}
                            value={selectedVehicles}
                            onChange={changeSelectedVehicles}
                        />
                        <span className={styles.metaCount}>
                            {isLoading
                                ? "Loading..."
                                : selectedVehicles.length
                                    ? `${totalItems} of ${vehicles.length} vehicles`
                                    : `${totalItems} vehicles`}
                        </span>
                        <span
                            className={`${styles.metaDownload} custom-Btn-animation`}
                            title={`Download ${depot} depot vehicles`}
                            onClick={downloadVehicles}
                        >
                            <DownloadIcon size={14} />
                        </span>
                    </div>

                    <div className={styles.popoverTableWrapper}>
                        <table className={`table table-bordered table-hover align-middle mb-0 ${styles.mainTable} ${styles.popoverTable}`}>
                            <colgroup>
                                <col className={styles.colVin} />
                                <col className={styles.colReg} />
                                <col className={styles.colStatus} />
                                <col className={styles.colFreq} />
                                {BUCKET_COLUMNS.map((b) => (
                                    <col key={b.key} className={styles.colBucket} />
                                ))}
                            </colgroup>

                            <thead className="sticky-top">
                                <tr>
                                    <th>VIN No.</th>
                                    <th>Reg No.</th>
                                    <th>Status</th>
                                    <th>Freq%</th>
                                    {BUCKET_COLUMNS.map((b) => (
                                        <th key={b.key}>{b.label}</th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={COL_COUNT}>
                                            <div className={styles.loadingState}>
                                                <span className={styles.spinner} />
                                                <span>Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : totalItems === 0 ? (
                                    <tr>
                                        <td colSpan={COL_COUNT}>
                                            <div className={styles.loadingState}>
                                                {vehicles.length ? "No vehicles match your selection" : "No vehicles found for this depot"}
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedVehicles.map((v, idx) => (
                                        <tr key={v.vin || idx} className={styles.locationRow}>
                                            <td className={styles.cityCell}>
                                                <span className={styles.vinText} title={v.vin}>{displayValue(v.vin)}</span>
                                            </td>
                                            <td>{displayValue(getRegNumber(v))}</td>
                                            <td><StatusBadge frequency={v.frequency} /></td>
                                            <td>{displayValue(v.frequency)}</td>
                                            {BUCKET_COLUMNS.map((b) => {
                                                // API rows use "0-5" … ">60"; avgDelay* is the fallback naming
                                                const value = v[b.field] ?? v[b.key];
                                                return (
                                                    <td
                                                        key={b.key}
                                                        className={`${styles.bucketCell} ${Number(value) ? TONE_CLASS[b.tone] : styles.tone_grey}`}
                                                    >
                                                        {displayValue(value)}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {!isLoading && (
                        <TablePagination
                            page={safePage}
                            totalPages={totalPages}
                            totalItems={totalItems}
                            pageSize={ROWS_PER_PAGE}
                            onPageChange={setCurrentPage}
                        />
                    )}
                </div>
            )}
        </CustomModal>
    );
}
